/**
 * L'ORDRE D'AFFRÈTEMENT — l'écran, 21/10/2026.
 *
 * Le document envoyé au SOUS-TRAITANT à qui TNS confie un transport. Demande
 * de Mohamed : « même modèle que la confirmation de commande ».
 *
 *
 * LE « MÊME MODÈLE » EST TENU PAR LE PARTAGE, PAS PAR LA RESSEMBLANCE
 * ---------------------------------------------------------------------------
 * Rien n'est recopié de la confirmation client :
 *
 *   - le CSS est le même, aux mêmes classes `conf-*` — une seule feuille de
 *     style dans le dépôt, tout préfixé, et c'est elle qui fait l'allure ;
 *   - la LIGNE D'ARRÊT et le formateur de nombres sont IMPORTÉS de
 *     `EcranConfirmation.tsx`, où ils ont été exportés pour ça ;
 *   - les calculs (charge, créneau, dates, repères, euros) viennent de
 *     `confirmation.ts`.
 *
 * Deux feuilles jumelles écrites deux fois auraient divergé à la première
 * correction faite d'un seul côté. Ici, corriger un créneau les corrige
 * toutes les deux.
 *
 *
 * CE QUI DIFFÈRE, ET POURQUOI
 * ---------------------------------------------------------------------------
 *   - LE DESTINATAIRE est le transporteur, pas le client ;
 *   - LE MONTANT est le PRIX D'ACHAT, annoncé FERME. Vérifié :
 *     `affretements` ne porte aucune clause d'indexation gasoil — elle vit sur
 *     `grilles_tarifaires`, du côté client. Le prix est donc ferme par
 *     construction, et sans cette phrase un sous-traitant refacture un gasoil ;
 *   - LES MENTIONS sont RENVERSÉES : la feuille client dit ce qui est compris
 *     dans le prix, celle-ci dit ce qu'on EXIGE ;
 *   - PAS DE TEMPS DE MANUTENTION ANNONCÉ : le contrat type qu'on annonce au
 *     client est ce qu'on lui ACCORDE. L'annoncer au sous-traitant serait lui
 *     accorder la même franchise, donc s'interdire de lui répercuter une
 *     immobilisation que le client nous facturerait. Le document dit l'inverse :
 *     les créneaux sont impératifs ;
 *   - PAS DE TVA, PAS DE TTC, comme la feuille client depuis le 24/09.
 *
 * LE BANDEAU NE PORTE QUE DEUX CASES depuis le 21/10/2026, sur demande de
 * Mohamed : « réf. à présenter » et « notre règlement » en sont partis. La
 * référence du client et nos délais de paiement restent rendus par l'API — ce
 * ne sont pas des secrets — mais ils n'ont plus à occuper le haut d'un
 * document dont le sujet est ce qu'on commande, pas ce qu'on doit.
 *
 * AUCUN PRIX DE VENTE N'ARRIVE JUSQU'ICI, et ce n'est pas cet écran qui le
 * garantit : c'est l'API, qui ne le sélectionne pas. Voir l'avertissement en
 * tête de `apps/api/src/ordre_affretement.ts`.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Arret, nombre } from './EcranConfirmation.js';
import { LogoTns } from './LogoTns.js';
import { centimes, charge, euros, jourLong } from './confirmation.js';
import { lireOrdreAffretement, type OrdreAffretementLu } from './ordreAffretement.js';

/**
 * L'émetteur, repris mot pour mot de la feuille client.
 *
 * En dur pour la raison déjà écrite au lot 116 : la table `societes` ne porte
 * que `raison_sociale`, `siret` et `etat` — ni adresse, ni TVA. Le jour où elle
 * les portera, c'est cet objet et son jumeau d'`EcranConfirmation.tsx` qui
 * disparaissent ensemble.
 *
 * `facture` est l'adresse où le sous-traitant envoie sa facture. Elle est
 * distincte de `mail`, qui est l'exploitation : une facture qui arrive chez
 * l'exploitation se perd.
 */
const TNS = {
  rs: 'TNS LOGISTIQUE',
  l1: '2 route de Gasville — 28630 Nogent-le-Phaye',
  l2: 'SARL au capital de 17 048 € — RCS Chartres 910 696 004',
  l3: 'SIRET 910 696 004 00027 — NAF 4941A',
  l4: 'Identifiant TVA : FR89 910 696 004',
  service: 'Service exploitation',
  mail: 'exploitation@tns-logistique.com',
  facture: 'facture.transport@tns-logistique.com',
  tel: 'Tél. : +33 2 34 40 33 13',
};

/** La hauteur d'une page A4 à 96 ppp, la même que la feuille au minimum. */
const HAUTEUR_A4 = 1123;

type Etat =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'refus'; readonly message: string }
  | { readonly nom: 'prete'; readonly lue: OrdreAffretementLu };

interface Proprietes {
  readonly jeton: () => Promise<string | null>;
  readonly otId: string;
  readonly quandFermer: () => void;
  readonly quandAccesPerdu: () => void;
}

/** Le portail, pour la raison expliquée dans `EcranConfirmation.tsx`. */
function Voile({ children }: { readonly children: React.ReactNode }) {
  return createPortal(
    <div className="application conf-racine">
      <div className="conf-voile">{children}</div>
    </div>,
    document.body,
  );
}

export function EcranOrdreAffretement({
  jeton,
  otId,
  quandFermer,
  quandAccesPerdu,
}: Proprietes) {
  const [etat, setEtat] = useState<Etat>({ nom: 'chargement' });

  const charger = useCallback(async () => {
    const j = await jeton();
    if (j === null) {
      quandAccesPerdu();
      return;
    }
    const resultat = await lireOrdreAffretement(j, otId);
    if (resultat.etat !== 'ok') {
      if (resultat.cause === 'acces') {
        quandAccesPerdu();
        return;
      }
      setEtat({
        nom: 'refus',
        message: resultat.message ?? 'L’ordre d’affrètement n’a pas pu être lu.',
      });
      return;
    }
    setEtat({ nom: 'prete', lue: resultat.valeur });
  }, [jeton, otId, quandAccesPerdu]);

  useEffect(() => {
    void charger();
  }, [charger]);

  // ÉCHAP FERME, comme partout ailleurs dans le logiciel.
  useEffect(() => {
    const touche = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') quandFermer();
    };
    window.addEventListener('keydown', touche);
    return () => {
      window.removeEventListener('keydown', touche);
    };
  }, [quandFermer]);

  if (etat.nom !== 'prete') {
    return (
      <Voile>
        <div className="conf-outils">
          <b>Ordre d’affrètement</b>
          <button className="bouton" type="button" onClick={quandFermer}>
            Fermer
          </button>
        </div>
        <p className={etat.nom === 'refus' ? 'bandeau-refus' : 'attente'}>
          {etat.nom === 'refus' ? etat.message : 'Chargement…'}
        </p>
      </Voile>
    );
  }

  return (
    <Voile>
      <div className="conf-outils">
        <b>Ordre d’affrètement {etat.lue.ordre.numero}</b>
        <button className="bouton" type="button" onClick={quandFermer}>
          Fermer
        </button>
        <button
          className="bouton conf-primaire"
          type="button"
          onClick={() => {
            window.print();
          }}
        >
          Imprimer / Enregistrer en PDF
        </button>
        <span className="conf-aide">
          Le PDF est produit par le navigateur : choisissez « Enregistrer au format PDF
          ».
        </span>
      </div>

      <FeuilleOrdreAffretement lue={etat.lue} />
    </Voile>
  );
}

/**
 * LA FEUILLE SEULE, sans le chargement ni la barre d'outils.
 *
 * Séparée pour la même raison que `FeuilleConfirmation` : elle NE FAIT QUE
 * DESSINER, donc elle est montrable dans l'aperçu — donc VÉRIFIABLE sans base
 * ni jeton.
 */
export function FeuilleOrdreAffretement({
  lue,
}: {
  readonly lue: OrdreAffretementLu;
}) {
  /** Le nombre de pages est MESURÉ, pas supposé. Même raison qu'au lot 116. */
  const [pages, setPages] = useState(1);
  const feuille = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const cadre = feuille.current;
    if (cadre === null) return;
    setPages(Math.max(1, Math.ceil(cadre.getBoundingClientRect().height / HAUTEUR_A4)));
  }, [lue]);

  const { ordre, affretement, transporteur, etapes } = lue;
  const chargements = etapes.filter((e) => e.type === 'chargement');
  const livraisons = etapes.filter((e) => e.type === 'livraison');
  const c = charge(etapes);

  const achat = centimes(affretement.prix_achat) ?? 0;
  const premier = chargements[0];
  const siren = transporteur.siret === null ? null : transporteur.siret.slice(0, 9);

  return (
    <div className="conf-cadre">
      <div className="conf-feuille" ref={feuille}>
        <div className="conf-corps">
          <div className="conf-haut">
            <div className="conf-logo">
              <LogoTns />
            </div>
            <div className="conf-emetteur">
              <div className="conf-rs">{TNS.rs}</div>
              {TNS.l1}
              <br />
              {TNS.l2}
              <br />
              {TNS.l3}
              <br />
              {TNS.l4}
            </div>
          </div>

          {/* LE TITRE N'EST PAS « CONFIRMATION ». Dans Stafa, « confirmation
                d'affrètement » désigne le document que TNS REÇOIT d'un donneur
                d'ordre. Celui-ci est un ORDRE. */}
          <div className="conf-titre">
            <div className="conf-t">Ordre d’affrètement</div>
          </div>

          <div className="conf-milieu">
            <div className="conf-renseignement">
              <div className="conf-souligne">Pour tout renseignement :</div>
              {TNS.service}
              <br />
              {TNS.mail}
              <br />
              {TNS.tel}
            </div>
            <div className="conf-client">
              <div className="conf-boite">
                <div className="conf-nom">{transporteur.nom}</div>
                {transporteur.code !== null && (
                  <div className="conf-cpt">Compte {transporteur.code}</div>
                )}
                <div className="conf-adr">
                  {transporteur.adresse ?? ''}
                  <br />
                  {transporteur.code_postal ?? ''} {transporteur.ville ?? ''}
                  <br />
                  {transporteur.pays ?? ''}
                </div>
              </div>
              <div className="conf-sous">
                <span>
                  {siren === null ? '' : `SIREN ${siren}`}
                  {siren !== null && transporteur.tva !== null ? ' — ' : ''}
                  {transporteur.tva === null ? '' : `TVA ${transporteur.tva}`}
                </span>
                <span>Page 1/{String(pages)}</span>
              </div>
            </div>
          </div>

          {/* DEUX CASES, ET PLUS QUATRE — 21/10/2026.

                « Réf. à présenter » et « Notre règlement » ont été retirées
                sur demande de Mohamed. Le sujet de ce document est CE QU'ON
                COMMANDE ; nos délais de paiement relèvent du contrat, et la
                référence du client n'avait pas à occuper le haut de page.

                LE VIDE N'EST PAS COMBLÉ par des cases de remplissage : deux
                cases larges se lisent mieux que quatre dont deux n'apprennent
                rien. */}
          <table className="conf-bandeau">
            <tbody>
              <tr>
                <td>
                  <div className="conf-quoi">Ordre n° :</div>
                  <div className="conf-val">{ordre.numero}</div>
                </td>
                {/* LA DATE DE L'ORDRE, pas celle du jour — contrairement à la
                      feuille client, dont c'est un défaut connu. Un ordre
                      réimprimé trois semaines plus tard doit porter la date à
                      laquelle il a été passé. */}
                <td>
                  <div className="conf-quoi">Date de l’ordre :</div>
                  <div className="conf-val">{jourLong(ordre.ordonne_le)}</div>
                </td>
              </tr>
            </tbody>
          </table>

          <table className="conf-lignes">
            <thead>
              <tr>
                <th style={{ width: '8.5%' }}>Date</th>
                <th style={{ width: '10.5%' }}>Arrêt</th>
                <th style={{ width: '48%' }}>Lieu et adresse</th>
                <th style={{ width: '14%' }}>Créneau</th>
                <th className="conf-n" style={{ width: '19%' }}>
                  Charge
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="conf-groupe">
                <td colSpan={5}>Enlèvement{chargements.length > 1 ? 's' : ''} :</td>
              </tr>
              {chargements.map((e) => (
                <Arret key={`c${String(e.rang)}`} e={e} combien={chargements.length} />
              ))}
              <tr className="conf-soustotal">
                <td colSpan={4}>Total à enlever</td>
                <td className="conf-n">
                  {nombre(c.quantite)} {c.support ?? ''} — {nombre(c.poids)} kg —{' '}
                  {nombre(c.metres, 2)} ml
                </td>
              </tr>
              <tr className="conf-groupe">
                <td colSpan={5}>Livraison{livraisons.length > 1 ? 's' : ''} :</td>
              </tr>
              {livraisons.map((e) => (
                <Arret key={`l${String(e.rang)}`} e={e} combien={livraisons.length} />
              ))}
            </tbody>
          </table>

          <div className="conf-pied">
            <table className="conf-moyen">
              <thead>
                <tr>
                  <th colSpan={2}>Marchandise et moyen</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Nature</td>
                  <td className="conf-q">{ordre.marchandise ?? '—'}</td>
                </tr>
                {/* LE MATÉRIEL EXIGÉ, et le mot compte : c'est ce qu'on
                      commande, pas ce que le transporteur possède. */}
                <tr>
                  <td>Véhicule exigé</td>
                  <td className="conf-q">{ordre.vehicule ?? '—'}</td>
                </tr>
                <tr>
                  <td>Votre contact</td>
                  <td className="conf-q">
                    {affretement.contact_nom ?? '—'}
                    {affretement.contact_telephone === null
                      ? ''
                      : ` — ${affretement.contact_telephone}`}
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="conf-totaux">
              <div className="conf-l">
                <span>Prix convenu HT</span>
                <span>{euros(achat)}</span>
              </div>
              {/* CETTE LIGNE OCCUPE LA PLACE EXACTE de « Indexation gazole »
                    sur la feuille client, et joue le même rôle : dire ce que
                    le prix contient avant d'annoncer le total.

                    Elle dit « inclus » là où la feuille client dit « en sus »
                    ou « comprise », et c'est tout l'écart entre les deux
                    documents — vérifié : `affretements` ne porte aucune clause
                    d'indexation, elle vit sur `grilles_tarifaires`, du côté
                    client. Sans cette ligne, rien n'empêche un sous-traitant
                    de refacturer un gasoil le mois suivant.

                    LIBELLÉ POSÉ PAR MOHAMED LE 21/10 : « Indexation gasoil ».
                    La feuille client écrit « Indexation gazole » — les deux
                    orthographes cohabitent donc dans le dépôt. */}
              <div className="conf-l">
                <span>Indexation gasoil</span>
                <span>inclus</span>
              </div>
              <div className="conf-l conf-net">
                <span>TOTAL HT</span>
                <span>{euros(achat)} EUR</span>
              </div>
            </div>
          </div>

          {/* LES CONSIGNES DE L'EXPLOITATION, quand il y en a.
                `affretements.instructions` est le seul champ libre de
                l'affrètement, et il porte ce qui a été convenu au téléphone.
                Le bloc disparaît quand il est vide : un encadré « Consignes : »
                suivi de rien ferait douter qu'il en manque. */}
          {affretement.instructions !== null && (
            <div className="conf-consignes">
              <b>Consignes particulières :</b> {affretement.instructions}
            </div>
          )}

          {/* LES MENTIONS SONT RENVERSÉES par rapport à la feuille client :
                celle-ci dit ce qu'on EXIGE.

                AUCUN TEMPS DE MANUTENTION N'EST ANNONCÉ, et c'est délibéré :
                le contrat type qu'on annonce au client est ce qu'on lui
                ACCORDE. L'accorder aussi au sous-traitant nous interdirait de
                lui répercuter une immobilisation que le client nous facture.

                LA CLAUSE DE SOUS-TRAITANCE PORTE SA SANCTION depuis le
                21/10/2026, à la demande de Mohamed. Une interdiction sans
                conséquence écrite ne vaut rien : celle-ci dit ce qui se passe
                si elle est enfreinte — contrat caduc, et règlement direct du
                transporteur qui a réellement roulé, déduit de ce qui est dû.
                C'est aussi la parade au risque de l'action directe, qui
                permettrait au vrai transporteur impayé de se retourner contre
                TNS après avoir déjà payé l'intermédiaire. */}
          <div className="conf-mentions">
            <div className="conf-categorie">
              <b>Prix ferme et définitif</b>, incluant le gasoil, les péages et les
              temps de chargement et de déchargement. Aucun supplément, de quelque
              nature que ce soit, ne sera accepté sans notre accord écrit préalable.
            </div>
            <b>Sous-traitance interdite.</b> Le présent transport doit être exécuté par
            vos propres moyens. Toute sous-traitance, même partielle, est subordonnée à
            notre accord écrit préalable. <b>À défaut</b>, nous nous réservons le droit
            de tenir le présent contrat pour caduc et de régler directement le
            transporteur ayant effectivement exécuté le transport, les sommes ainsi
            versées venant en déduction de tout montant qui vous serait dû.
            <br />
            <b>Aucun contact avec le donneur d’ordre.</b> Les sites d’enlèvement et de
            livraison sont ceux de notre client. Toute prise de contact commerciale,
            directe ou indirecte, avec celui-ci est exclue.
            <br />
            <b>Documents de transport sous 48 heures.</b> La lettre de voiture ou
            l’eCMR signé et daté du destinataire, ainsi que le bon de livraison,
            doivent nous être transmis <b>dans les 48 heures suivant la livraison</b>.
            Aucune facture ne sera réglée sans ces documents.
            <br />
            <b>Les créneaux ci-dessus sont impératifs.</b> Tout retard, incident ou
            impossibilité doit nous être signalé IMMÉDIATEMENT au{' '}
            {TNS.tel.replace('Tél. : ', '')}, avant et non après l’heure prévue.
            <br />
            <b>Votre facture</b> doit porter la référence {ordre.numero}, être
            accompagnée des documents de transport, et être adressée à{' '}
            <b>{TNS.facture}</b>. Le présent ordre vaut accord sur les dates, les
            lieux, le matériel et le prix ci-dessus ; son exécution vaut acceptation
            sans réserve de ces conditions.
          </div>

          <div className="conf-talon">
            <div>
              <div className="conf-titre-talon">
                Ordre {ordre.numero}
                {transporteur.code === null ? '' : ` — Compte ${transporteur.code}`}
              </div>
              <div className="conf-gros">
                {euros(achat)} EUR HT
                {premier !== undefined && (
                  <span className="conf-maigre">
                    {' '}
                    — enlèvement le {jourLong(premier.jour)}
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="conf-titre-talon">Votre facture</div>
              <div>{TNS.facture}</div>
              <div>Référence à rappeler : {ordre.numero}</div>
              <div>{TNS.service} — {TNS.tel}</div>
            </div>
          </div>
        </div>

        <div className="conf-bande" />
      </div>
    </div>
  );
}
